import React from 'react';
import { Trophy, User, Hash, Mail } from 'lucide-react';

interface RankingItem {
  rank: number;
  name: string | null;
  value: string | null;
  id: string;
  email: string;
  description: string | null;
}

interface AnalyticsRankingMessageProps {
  content: string;
  structured_data?: {
    type: 'analytics_ranking';
    ranking_data: RankingItem[];
    total_results: number;
    metric_table: string;
    group_by_table: string;
    ranking_direction: 'ASC' | 'DESC';
    limit: number;
    analytics_type: 'ranking';
  };
}

export const AnalyticsRankingMessage: React.FC<AnalyticsRankingMessageProps> = ({
  content,
  structured_data
}) => {
  if (!structured_data || structured_data.type !== 'analytics_ranking') {
    return <div className="text-gray-600">{content}</div>;
  }

  const { ranking_data, total_results, metric_table, group_by_table } = structured_data;

  // Take top 10 for display
  const displayData = ranking_data.slice(0, 10);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Analytics Results</h3>
        </div>
        <p className="text-sm text-gray-600">
          {content}
        </p>
        <div className="flex gap-4 mt-2 text-xs text-gray-500">
          <span>📊 {total_results} total results</span>
          <span>📋 Grouped by {group_by_table}</span>
          <span>📈 Based on {metric_table}</span>
        </div>
      </div>

      {/* Ranking Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700">Top {displayData.length} Results</h4>
        </div>
        
        <div className="divide-y divide-gray-100">
          {displayData.map((item, index) => (
            <div 
              key={item.id} 
              className="px-4 py-3 hover:bg-gray-50 transition-colors duration-150"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Rank Badge */}
                  <div className={`
                    flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm
                    ${item.rank === 1 ? 'bg-yellow-100 text-yellow-700' : 
                      item.rank === 2 ? 'bg-gray-100 text-gray-700' :
                      item.rank === 3 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-600'}
                  `}>
                    {item.rank}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {item.email}
                      </span>
                      {item.name && (
                        <span className="text-xs text-gray-500">
                          ({item.name})
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                    )}
                  </div>
                </div>
                
                {/* Value Badge */}
                {item.value && (
                  <div className="flex items-center gap-1 px-3 py-1 bg-blue-50 rounded-full">
                    <Hash className="h-3 w-3 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-700">
                      {item.value}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Show More */}
        {total_results > displayData.length && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Showing {displayData.length} of {total_results} results
            </p>
          </div>
        )}
      </div>
    </div>
  );
};